import http from '../HttpWrapper'

export type StartKitConvoReq = {
    convId: string
    personId?: string
    dynamicVariables?: Record<string, string | boolean>
    agentId?: string
    source?: string
}

export type StartKitConvoRes = {
    convId: string
    personId: string | null
}

export type MarkKitConvoCallEndedReq = {
    convId: string
}

export type MarkKitConvoCallEndedRes = {
    convId: string
    kitConvoId: string | null
    status: KitConvoStatusDto | null
    updated: boolean
}

export type KitConvoToolCallDto = {
    id: string
    calledAt: string
    respondedAt: string | null
    toolName: string
    headers: string | null
    urlCalled: string
    urlQueryParams: string | null
    httpMethod: string
    body: string | null
    responseBody: string | null
    responseStatusCode: number | null
    responseDurationMs: number | null
}

export type KitConvoPersonMentionPersonDto = {
    id: string
    firstName: string
    middleName: string | null
    lastName: string | null
    linkedInUrl: string | null
    linkedInProfilePicUrl: string | null
    headline: string | null
    connectionSummary: string | null
}

export type KitConvoPersonMentionDto = {
    toolCallId: string
    calledAt: string
    persons: KitConvoPersonMentionPersonDto[]
    rating?: number | null
    ratingReason?: string | null
}

export type KitConvoCompanyMentionResolvedCompanyDto = {
    id: string
    name: string
}

export type KitConvoCompanyMentionDto = {
    toolCallId: string
    calledAt: string
    companyName: string | null
    matchingName: string | null
    resolvedCompanies: KitConvoCompanyMentionResolvedCompanyDto[]
}

export type KitConvoLocationMentionDto = {
    toolCallId: string
    calledAt: string
    city: string | null
    country: string | null
}

export type KitConvoTargetJobTitleMentionDto = {
    toolCallId: string
    calledAt: string
    jobTitle: string
}

export type KitConvoTargetSalaryMentionDto = {
    toolCallId: string
    calledAt: string
    minSalaryGBP: number | null
    maxSalaryGBP: number | null
    minSalaryUSD: number | null
    maxSalaryUSD: number | null
    minSalaryEUR: number | null
    maxSalaryEUR: number | null
    inputCurrency: 'GBP' | 'USD' | 'EUR'
}

export type KitConvoCurrentJobSearchParamsSourceCompanyMentionDto = {
    id: string
    companyName: string | null
    matchingName: string | null
    resolvedCompanies: KitConvoCompanyMentionResolvedCompanyDto[]
}

export type KitConvoCurrentJobSearchParamsSourceCompanyDto = {
    id: string
    name: string
    dataProviderId: string | null
}

export type KitConvoCurrentJobSearchParamsSourceLocationMentionDto = {
    id: string
    city: string | null
    country: string | null
}

export type KitConvoCurrentJobSearchParamsTargetSalaryDto = {
    minSalaryGBP: number | null
    maxSalaryGBP: number | null
    minSalaryUSD: number | null
    maxSalaryUSD: number | null
    minSalaryEUR: number | null
    maxSalaryEUR: number | null
    inputCurrency: 'GBP' | 'USD' | 'EUR'
}

export type KitConvoCurrentJobSearchParamsDto = {
    id: string
    sourceCompanies: KitConvoCurrentJobSearchParamsSourceCompanyDto[]
    sourceCompanyMentions: KitConvoCurrentJobSearchParamsSourceCompanyMentionDto[]
    targetJobTitles: string[]
    targetSalaries: KitConvoCurrentJobSearchParamsTargetSalaryDto[]
    generatedJobTitles: string[]
    sourceLocationMentions: KitConvoCurrentJobSearchParamsSourceLocationMentionDto[]
}

export type KitConvoJobSearchExecutionResultJobDto = {
    id: string
    coreSignalId: string
    title: string | null
    location: string | null
    companyName: string | null
    coreSignalJobCreatedAt: string | null
    createdAt: string
    updatedAt: string
}

export type KitConvoJobSearchExecutionResultDto = {
    id: string
    rankScore: number | null
    returnedToUser: boolean
    createdAt: string
    updatedAt: string
    job: KitConvoJobSearchExecutionResultJobDto | null
}

export type KitConvoJobSearchExecutionDto = {
    id: string
    toolCallId: string | null
    coreSignalFiltersJson: string
    createdAt: string
    updatedAt: string
    results: KitConvoJobSearchExecutionResultDto[]
}

export type KitConvoTranscriptRawDataDto = {
    id: string
    payloadJson: string
    eventType: string
    eventTimestamp: number | null
    createdAt: string
    updatedAt: string
}

export type KitConvoTranscriptTurnDto = {
    id: string
    index: number
    role: string
    message: string | null
    toolCallJson: string
    toolResultsJson: string
    isError: boolean
    elapsedSecondsSinceCallStart: number
    createdAt: string
}

export type KitConvoJobOfferedStatusDto =
    | 'Offered'
    | 'Interested'
    | 'Not Interested'

export type KitConvoStatusDto =
    | 'Live'
    | 'Call Ended'
    | 'Transcript Available'
    | 'No Transcription'

export type KitConvoJobOfferedDto = {
    coreSignalJobId: string
    coreSignalId: string
    title: string | null
    location: string | null
    companyName: string | null
    coreSignalJobCreatedAt: string | null
    status: KitConvoJobOfferedStatusDto
    reason: string | null
}

export type KitConvoDebugUserDto = {
    id: string
    email: string
    name: string | null
}

export type KitConvoDebugPersonDto = {
    id: string
    firstName: string
    middleName: string | null
    lastName: string | null
    linkedInProfilePicUrl: string | null
}

export type KitConvoDebugConversationDto = {
    id: string
    convId: string
    createdAt: string
    status: KitConvoStatusDto
    source: string | null
    dynamicVariables: string | null
    user: KitConvoDebugUserDto
    person: KitConvoDebugPersonDto | null
}

export type KitConvoDebugScoringCriterionDto = {
    id: string
    label: string
    shortName: string | null
    scoreType: 'binary' | 'pass-flag-fail' | 'numeric-1-3'
}

export type KitConvoDebugScoringRubricSectionDto = {
    id: string
    label: string
    sectionType: string
    criteria: KitConvoDebugScoringCriterionDto[]
}

export type KitConvoDebugCriterionScoreDto = {
    criterionId: string
    scoreValue: string
    justification: string
    detail: string | null
}

export type KitConvoDebugScoringAttemptDto = {
    id: string
    createdAt: string
    status: 'COMPLETED' | 'FAILED'
    llmModel: string
    errorMessage: string | null
    errorDetail: string | null
    scores: KitConvoDebugCriterionScoreDto[]
}

export type KitConvoDebugScoringDto = {
    attempt: KitConvoDebugScoringAttemptDto | null
    rubric: KitConvoDebugScoringRubricSectionDto[]
}

export type GetKitConvoDebugRes = {
    conversation: KitConvoDebugConversationDto | null
    toolCalls: KitConvoToolCallDto[]
    transcriptRawData: KitConvoTranscriptRawDataDto | null
    transcriptTurns: KitConvoTranscriptTurnDto[]
    personMentions: KitConvoPersonMentionDto[]
    usedToWorkAtCompanyMentions: KitConvoCompanyMentionDto[]
    targetJobLocationMentions: KitConvoLocationMentionDto[]
    targetJobTitleMentions: KitConvoTargetJobTitleMentionDto[]
    targetSalaryMentions: KitConvoTargetSalaryMentionDto[]
    currentJobSearchParams: KitConvoCurrentJobSearchParamsDto | null
    jobsOffered: KitConvoJobOfferedDto[]
    jobSearchExecutions: KitConvoJobSearchExecutionDto[]
    scoring: KitConvoDebugScoringDto | null
}

export type KitConvoListUserDto = {
    id: string
    email: string
    name: string | null
}

export type KitConvoListPersonDto = {
    id: string
    firstName: string
    middleName: string | null
    lastName: string | null
    linkedInProfilePicUrl: string | null
}

export type KitConvoListItem = {
    id: string
    convId: string
    createdAt: string
    status: KitConvoStatusDto
    dynamicVariables: string | null
    source: string | null
    networkKitThankYouEmailCount: number | null
    hasToolCalls: boolean
    jobsOfferedCount: number
    interestedJobsCount: number
    notInterestedJobsCount: number
    erroredToolCallCount: number
    user: KitConvoListUserDto
    person: KitConvoListPersonDto | null
}

export type GetKitConvoListRes = {
    convos: KitConvoListItem[]
    sources: string[]
    totalTranscriptCount: number
    availableTranscriptCount: number
    missingTranscriptCount: number
    pageNumber: number
    perPage: number
    hasMore: boolean
}

export type NetworkKitThankYouEmailHistoryConvo = {
    kitConvoId: string
    convId: string
    networkKitThankYouEmailCount: number
}

export type GetNetworkKitThankYouEmailPreviewRes = {
    convId: string
    kitConvoId: string
    userEmail: string
    currentConvoThankYouEmailCount: number
    otherConvosWithThankYou: NetworkKitThankYouEmailHistoryConvo[]
}

export type SendNetworkKitThankYouEmailReq = {
    convId: string
}

export type SendNetworkKitThankYouEmailRes = {
    convId: string
    kitConvoId: string | null
    sent: boolean
    recipientEmail: string | null
    coreSignalJobsCount: number
    networkKitThankYouEmailCount: number | null
}

export type BackfillKitConvoTranscriptReq = {
    conversationId: string
}

export type BackfillKitConvoTranscriptRes = {
    conversationId: string
    kitConvoId: string | null
    turnsStored: number
    backfillStatus: 'stored' | 'not_ready'
    conversationStatus: string | null
    message: string | null
}

export type RetriggerScoringReq = {
    kitConvoId: string
}

export type RetriggerScoringRes = {
    kitConvoId: string
    status: 'completed' | 'failed'
    message: string | null
}

export type BulkRetriggerScoringReq = {
    source: string
}

export type BulkRetriggerScoringRes = {
    total: number
    succeeded: number
    failed: number
    errors: { kitConvoId: string; message: string }[]
}

export type KitScreeningJobDto = {
    id: string
    createdAt: string
    updatedAt: string
    createdBy: string
    updatedBy: string
    country: string
    jobTitle: string
    clientCompanyId: string | null
    clientFreehandName: string | null
    jobNumber: string | null
    agentId: string
    urlId: string
    isActive: boolean
    rubricSectionCount: number
    rubricCriteriaCount: number
}

export type ParseRubricReq = { screeningJobId: string; rubricText: string }
export type ParsedRubricCriterion = {
    label: string
    passDescription: string | null
    flagDescription: string | null
    failDescription: string | null
    score1Description: string | null
    score2Description: string | null
    score3Description: string | null
}
export type ParsedRubricSection = {
    sectionType: string
    label: string
    criteria: ParsedRubricCriterion[]
}
export type ParseRubricRes = { sections: ParsedRubricSection[] }
export type SaveRubricReq = {
    screeningJobId: string
    sections: ParsedRubricSection[]
}
export type SaveRubricRes = { sectionCount: number; criteriaCount: number }
export type GetRubricRes = { sections: ParsedRubricSection[] }

export type KitJobAdDto = {
    id: string
    createdAt: string
    updatedAt: string
    createdBy: string
    updatedBy: string
    name: string
    workEmail: string
    jobTitle: string
    linkedinUrl: string | null
}

export type GetKitJobAdListRes = {
    jobAds: KitJobAdDto[]
    pageNumber: number
    perPage: number
    hasMore: boolean
}

export type KitCoreSignalJobDto = {
    id: string
    coreSignalId: string
    title: string | null
    location: string | null
    companyName: string | null
    coreSignalJobCreatedAt: string | null
    hasJobDetails: boolean
    createdAt: string
    updatedAt: string
}

export type GetKitCoreSignalJobListRes = {
    jobs: KitCoreSignalJobDto[]
    pageNumber: number
    perPage: number
    hasMore: boolean
}

export type KitCoreSignalJobDetailsFilter = 'all' | 'has_details' | 'no_details'

export type KitCoreSignalJobDetailsDto = {
    id: string
    coreSignalId: string
    title: string | null
    location: string | null
    companyName: string | null
    coreSignalJobCreatedAt: string | null
    hasJobDetails: boolean
    jobDetailFetchedAt: string | null
    jobDetail: Record<string, unknown> | null
}

export type GetKitCoreSignalJobDetailsReq = {
    coreSignalJobId: string
    fetchIfMissing?: boolean
}

export type GetKitCoreSignalJobDetailsRes = {
    job: KitCoreSignalJobDetailsDto
}

export type GetKitScreeningJobListRes = {
    jobs: KitScreeningJobDto[]
    pageNumber: number
    perPage: number
    hasMore: boolean
}

export type KitScreeningJobPublicDto = {
    jobTitle: string
    isActive: boolean
}

export type GetKitScreeningJobPublicRes = {
    job: KitScreeningJobPublicDto | null
}

export type KitScreeningJobJobSeekerDto = {
    id: string
    jobTitle: string
    agentId: string
    urlId: string
    isActive: boolean
    clientCompanyId: string | null
    clientFreehandName: string | null
}

export type GetKitScreeningJobRes = {
    job: KitScreeningJobJobSeekerDto | null
}

export type CreateKitScreeningJobReq = {
    country: string
    jobTitle: string
    clientCompanyId?: string
    clientFreehandName?: string
    jobNumber?: string
    agentId: string
    urlId: string
    isActive?: boolean
    tenantId?: string
}

export type CreateKitScreeningJobRes = {
    jobId: string
}

export type UpdateKitScreeningJobReq = {
    id: string
    country: string
    jobTitle: string
    clientCompanyId?: string
    clientFreehandName?: string
    jobNumber?: string
    agentId: string
    urlId: string
    isActive: boolean
    tenantId?: string
}

// User-facing screener types
export type ScreenerListItemUserDto = {
    id: string
    jobTitle: string
    isActive: boolean
    createdAt: string
    urlId: string
    candidateCount: number
}

export type GetScreenerListUserRes = {
    screeners: ScreenerListItemUserDto[]
}

export type ScreenerCandidateDto = {
    id: string
    name: string
    email: string | null
    phone: string | null
    linkedInUrl: string | null
    profilePicUrl: string | null
    overallPercentage: number | null
    scores: Record<string, { value: string | null; detail: string | null }>
    hasScores: boolean
}

export type ScreenerRubricSectionDto = {
    id: string
    label: string
    scoreType: 'binary' | 'pass-flag-fail' | 'numeric-1-3'
    criteria: { id: string; label: string; shortName: string | null }[]
}

export type GetScreenerDetailUserRes = {
    id: string
    jobTitle: string
    isActive: boolean
    createdAt: string
    urlId: string
    rubric: { sections: ScreenerRubricSectionDto[] }
    candidates: ScreenerCandidateDto[]
}

export type KitJobAdSignupReq = {
    name: string
    workEmail: string
    jobTitle: string
    linkedinUrl?: string
}

export type KitJobAdSignupRes = {
    userId: string | null
    createdNewUser: boolean
    requiresLogin: boolean
    jobAdId: string
}

class KitApi {
    private static instance: KitApi

    public constructor() {
        if (KitApi.instance) {
            return KitApi.instance
        }

        KitApi.instance = this
    }

    public async startConvo(req: StartKitConvoReq): Promise<StartKitConvoRes> {
        return await http.post<StartKitConvoReq, StartKitConvoRes>(
            '/kit/start-convo',
            req,
        )
    }

    public async startConvoSelf(
        req: StartKitConvoReq,
    ): Promise<StartKitConvoRes> {
        return await http.post<StartKitConvoReq, StartKitConvoRes>(
            '/kit/start-convo-self',
            req,
        )
    }

    public async startConvoHm(
        req: StartKitConvoReq,
    ): Promise<StartKitConvoRes> {
        return await http.post<StartKitConvoReq, StartKitConvoRes>(
            '/kit/start-convo-hm',
            req,
        )
    }

    public async markConvoCallEnded(
        req: MarkKitConvoCallEndedReq,
    ): Promise<MarkKitConvoCallEndedRes> {
        return await http.post<
            MarkKitConvoCallEndedReq,
            MarkKitConvoCallEndedRes
        >('/kit/mark-convo-call-ended', req)
    }

    public async getConvoDebug(convId: string): Promise<GetKitConvoDebugRes> {
        const queryParams = new URLSearchParams({ convId })
        return await http.get<GetKitConvoDebugRes>(
            '/kit/convo-debug',
            queryParams,
        )
    }

    public async getConvosAdmin(params?: {
        pageNumber?: number
        perPage?: number
        source?: string
    }): Promise<GetKitConvoListRes> {
        const queryParams = new URLSearchParams()
        if (typeof params?.pageNumber === 'number') {
            queryParams.set('pageNumber', String(params.pageNumber))
        }
        if (typeof params?.perPage === 'number') {
            queryParams.set('perPage', String(params.perPage))
        }
        if (typeof params?.source === 'string' && params.source.trim().length) {
            queryParams.set('source', params.source.trim())
        }
        return await http.get<GetKitConvoListRes>(
            '/kit/convos-admin',
            queryParams,
        )
    }

    public async getNetworkKitThankYouEmailPreview(
        convId: string,
    ): Promise<GetNetworkKitThankYouEmailPreviewRes> {
        const queryParams = new URLSearchParams({ convId })

        return await http.get<GetNetworkKitThankYouEmailPreviewRes>(
            '/kit/network-kit-thank-you-email-preview',
            queryParams,
        )
    }

    public async sendNetworkKitThankYouEmail(
        req: SendNetworkKitThankYouEmailReq,
    ): Promise<SendNetworkKitThankYouEmailRes> {
        return await http.post<
            SendNetworkKitThankYouEmailReq,
            SendNetworkKitThankYouEmailRes
        >('/kit/send-network-kit-thank-you-email', req)
    }

    public async exportAllConvoTranscriptsCsv(params?: {
        source?: string
        fetchMissing?: boolean
    }): Promise<Blob> {
        const queryParams = new URLSearchParams()

        if (typeof params?.source === 'string' && params.source.trim().length) {
            queryParams.set('source', params.source.trim())
        }

        if (typeof params?.fetchMissing === 'boolean') {
            queryParams.set('fetchMissing', String(params.fetchMissing))
        }

        return await http.get<Blob>('/kit/convos-export-all-csv', queryParams, {
            responseType: 'blob',
        })
    }

    public async exportConvoCsv(convId: string): Promise<Blob> {
        const queryParams = new URLSearchParams({ convId })

        return await http.get<Blob>('/kit/convo-export-csv', queryParams, {
            responseType: 'blob',
        })
    }

    public async backfillConvoTranscript(
        req: BackfillKitConvoTranscriptReq,
    ): Promise<BackfillKitConvoTranscriptRes> {
        return await http.post<
            BackfillKitConvoTranscriptReq,
            BackfillKitConvoTranscriptRes
        >('/kit/backfill-convo-transcript', req)
    }

    public async retriggerScoring(
        req: RetriggerScoringReq,
    ): Promise<RetriggerScoringRes> {
        return await http.post<RetriggerScoringReq, RetriggerScoringRes>(
            '/kit/retrigger-scoring',
            req,
        )
    }

    public async bulkRetriggerScoring(
        req: BulkRetriggerScoringReq,
    ): Promise<BulkRetriggerScoringRes> {
        return await http.post<
            BulkRetriggerScoringReq,
            BulkRetriggerScoringRes
        >('/kit/bulk-retrigger-scoring', req)
    }

    public async getScreeningJobs(params?: {
        pageNumber?: number
        perPage?: number
    }): Promise<GetKitScreeningJobListRes> {
        const queryParams = new URLSearchParams()
        if (typeof params?.pageNumber === 'number') {
            queryParams.set('pageNumber', String(params.pageNumber))
        }
        if (typeof params?.perPage === 'number') {
            queryParams.set('perPage', String(params.perPage))
        }
        return await http.get<GetKitScreeningJobListRes>(
            '/kit/screening-jobs',
            queryParams,
        )
    }

    public async getJobAds(params?: {
        pageNumber?: number
        perPage?: number
    }): Promise<GetKitJobAdListRes> {
        const queryParams = new URLSearchParams()
        if (typeof params?.pageNumber === 'number') {
            queryParams.set('pageNumber', String(params.pageNumber))
        }
        if (typeof params?.perPage === 'number') {
            queryParams.set('perPage', String(params.perPage))
        }
        return await http.get<GetKitJobAdListRes>('/kit/job-ads', queryParams)
    }

    public async getCoreSignalJobs(params?: {
        pageNumber?: number
        perPage?: number
        detailsFilter?: KitCoreSignalJobDetailsFilter
    }): Promise<GetKitCoreSignalJobListRes> {
        const queryParams = new URLSearchParams()
        if (typeof params?.pageNumber === 'number') {
            queryParams.set('pageNumber', String(params.pageNumber))
        }
        if (typeof params?.perPage === 'number') {
            queryParams.set('perPage', String(params.perPage))
        }
        if (params?.detailsFilter) {
            queryParams.set('detailsFilter', params.detailsFilter)
        }
        return await http.get<GetKitCoreSignalJobListRes>(
            '/kit/core-signal-jobs',
            queryParams,
        )
    }

    public async getCoreSignalJobDetails(
        req: GetKitCoreSignalJobDetailsReq,
    ): Promise<GetKitCoreSignalJobDetailsRes> {
        return await http.post<
            GetKitCoreSignalJobDetailsReq,
            GetKitCoreSignalJobDetailsRes
        >('/kit/core-signal-job-details', req)
    }

    public async getScreeningJobPublic(
        urlId: string,
    ): Promise<GetKitScreeningJobPublicRes> {
        const queryParams = new URLSearchParams({ urlId })
        return await http.get<GetKitScreeningJobPublicRes>(
            '/kit/screening-job-public',
            queryParams,
            undefined,
            false,
        )
    }

    public async getScreeningJob(
        urlId: string,
    ): Promise<GetKitScreeningJobRes> {
        const queryParams = new URLSearchParams({ urlId })
        return await http.get<GetKitScreeningJobRes>(
            '/kit/screening-job',
            queryParams,
        )
    }

    public async createScreeningJob(
        req: CreateKitScreeningJobReq,
    ): Promise<CreateKitScreeningJobRes> {
        return await http.post<
            CreateKitScreeningJobReq,
            CreateKitScreeningJobRes
        >('/kit/create-screening-job', req)
    }

    public async updateScreeningJob(
        req: UpdateKitScreeningJobReq,
    ): Promise<undefined> {
        return await http.post<UpdateKitScreeningJobReq, undefined>(
            '/kit/update-screening-job',
            req,
        )
    }

    public async submitJobAdSignup(
        req: KitJobAdSignupReq,
    ): Promise<KitJobAdSignupRes> {
        return await http.post<KitJobAdSignupReq, KitJobAdSignupRes>(
            '/kit/job-ad-signup',
            req,
            undefined,
            undefined,
            false,
        )
    }

    public async getRubric(screeningJobId: string): Promise<GetRubricRes> {
        const queryParams = new URLSearchParams({ screeningJobId })
        return await http.get<GetRubricRes>('/kit/get-rubric', queryParams)
    }

    public async parseRubric(req: ParseRubricReq): Promise<ParseRubricRes> {
        return await http.post<ParseRubricReq, ParseRubricRes>(
            '/kit/parse-rubric',
            req,
        )
    }

    public async saveRubric(req: SaveRubricReq): Promise<SaveRubricRes> {
        return await http.post<SaveRubricReq, SaveRubricRes>(
            '/kit/save-rubric',
            req,
        )
    }

    public async getScreenersForUser(): Promise<GetScreenerListUserRes> {
        return await http.get<GetScreenerListUserRes>(
            '/kit/screening-jobs-user',
        )
    }

    public async getScreenerDetailForUser(
        screeningJobId: string,
    ): Promise<GetScreenerDetailUserRes> {
        const queryParams = new URLSearchParams({ screeningJobId })
        return await http.get<GetScreenerDetailUserRes>(
            '/kit/screener-detail-user',
            queryParams,
        )
    }
}

const kitApi = new KitApi()
export default kitApi
